import { BehaviorSubject, Subject } from 'rxjs';

import { ClipboardCommunicator } from './clipboard-communicator';
import { Connection, CPAR } from './connection';
import { Connector } from './connector';
import { Databin, DTB } from './databin';
import { FilesystemCommunicator } from './filesystem-communicator';
import { LinkCommunicator } from './link-communicator';
import { OnlineCommunicator } from './online-communicator';
import { ProjectCommunicator } from './project-communicator';
import { RssCommunicator } from './rss-communicator';
import { XItem } from './x-item';

import type { CoordinatorService } from './coordinator.service';
import { ParserFile, PFPARAMS } from './parser-file';
import { ScriptInput } from './script-input';
export interface Communicator {
    tag: string;
    connection: Connection | undefined;
    update$: Subject<void>;
    fetch?: (fetchStep: number) => void;
}

type CC =
    | OnlineCommunicator
    | FilesystemCommunicator
    | ClipboardCommunicator
    | RssCommunicator
    | LinkCommunicator
    | ProjectCommunicator;

export class Databin$ {
    isReady = false; // set by init parser-worker

    private _parserFileMap = new Map<string, ParserFile>();

    communicatorStage$ = new BehaviorSubject<number>(1);
    // communicatorMessage$ = new BehaviorSubject<any>(null)

    communicatorMap = new Map<string, CC>();
    // communicatorUnconfirmedLs: XItem[] = []

    private _dH?: FileSystemDirectoryHandle;

    refresh$ = new Subject<void>();

    dialog$ = new Subject();
    preview$ = new Subject();
    // current$ = new BehaviorSubject<Databin | undefined>(undefined)
    private _current: Databin | undefined;

    editedConnection$ = new BehaviorSubject<Connection | undefined>(undefined);
    connectionWait$ = new BehaviorSubject<string | undefined>(undefined);
    // parsedFileLs$ = new Subject<{
    //     connection: Connection,
    //     pfLs: ParsedFile[]
    // } | undefined>()

    private _databinMap = new Map<string, Databin>();
    private _connectorMap = new Map<string, Connector>();

    // xitemMap = new Map<string, XItem>()
    activeXitemIdSet = new Set<string>();

    constructor(private coords: CoordinatorService) {
        fetch('/assets/connectors.json')
            .then((x) => x.json())
            .then((ls: any[]) => {
                ls.forEach((x) => {
                    if (!x.tag.startsWith('_')) new Connector(this.coords, x);
                });
            });
    }

    public get current(): Databin | undefined {
        if (!this._current) this.current = this.sortedDatabinLs[0];
        if (!this._current) this.activateFirstDatabin();
        return this._current;
    }
    public set current(value: Databin | undefined) {
        this._current = value;
        this.coords.display$.markForCheck();
    }

    createParserFile(params: PFPARAMS) {
        const pf = new ParserFile(this.coords, params);
        this._parserFileMap.set(pf.id, pf);
        return pf;
    }

    onStart() {
        this.coords.worker$.parser.reportSuccess$.subscribe({
            next: (x) => {
                if (!x) return;
                if (x.action === 'initialized') {
                    this.coords.databin$.isReady = true;
                    this.coords.display$.markForCheck();
                    return;
                }

                const pF = this._parserFileMap.get(x.parserFileId);
                pF && pF.response(x.resp);
            },
            error: (e) => console.log(e),
        });

        this.coords.worker$.parser.reportError$.subscribe({
            next: (x) => {
                if (!x) return;
                const pF = this._parserFileMap.get(x.parserFileId);
                pF && pF.error(x.error);
            },
            error: (e) => console.log(e),
        });

        [
            new FilesystemCommunicator(this.coords),
            new ClipboardCommunicator(this.coords),
            new RssCommunicator(this.coords),
            new LinkCommunicator(this.coords),
            new OnlineCommunicator(this.coords),
            new ProjectCommunicator(this.coords),
        ].forEach((x) => this.communicatorMap.set(x.tag, x));

        this.communicatorStage$.subscribe((n) => {
            switch (n) {
                case 0:
                    this.coords.display$.closeDialog();
                    // this.communicatorUnconfirmedLs = []
                    this.coords.display$.markForCheck();
                    return;
                case 1:
                    const c = this.coords.databin$.editedConnection$.getValue();
                    c?.clear();
                    // this.communicatorUnconfirmedLs = []
                    // this.communicatorMessage$.next({})
                    break;
                case 2:
                    break;
                case 3:
                    break;
                case 4:
                    // const c = this.coords.databin$.editedConnection$.getValue()!

                    // if (c.isFetchError) {
                    //     this.coords.databin$.communicatorMessage$.next({
                    //         type: "error",
                    //         text: c.lastFetchErrorText
                    //     })
                    //     c.isFetchError = false
                    //     c.lastFetchErrorText = ""
                    // }
                    // else if (c.activeFetchNumber)
                    //     this.coords.databin$.communicatorMessage$.next({
                    //         type: "info",
                    //         text: this.coords.text("FetchInProgress")
                    //     })
                    // else {
                    //     this.coords.databin$.communicatorMessage$.next({})
                    // }

                    break;
            }
            this.coords.display$.dialog$.next({
                action: 'connection-step',
                step: n,
            });
        });
    }

    // get communicatorPreviewLs(): XItem[] {
    //     return this.communicatorUnconfirmedLs.slice(0, 30)
    // }

    get hasDatabin() {
        return !!this._databinMap.size;
    }

    onlineDialog() {
        this.coords.display$.dialog$.next({
            action: 'connection-online',
        });
    }

    getConnection(connectionId: string) {
        const ec = this.editedConnection$.getValue();
        if (ec && ec.id === connectionId) return ec;
        for (let dtb of this.databinLs)
            for (let c of dtb.connectionLs) if (c.id === connectionId) return c;
        return null;
    }

    async dH() {
        if (!this._dH) {
            const h = await navigator.storage.getDirectory();
            this._dH = await h.getDirectoryHandle('_temp');
        }
        return this._dH;
    }

    async loadCacheFile(cacheId: string): Promise<XItem[]> {
        try {
            const dH = await this.dH();
            const fH = await dH.getFileHandle(cacheId);
            const f = await fH.getFile();
            const data = await this.coords.filesystem$.readFile(f);
            return JSON.parse(data.payload).map((x: any) => new XItem(this.coords, x));
        } catch (e) {
            console.log('loadCacheFile', e);
            return [];
        }
    }

    registerCommunicator(c: CC) {
        this.communicatorMap.set(c.tag, c);
    }

    getCommunicator(tag: string) {
        return this.communicatorMap.get(tag);
    }

    load3(ls: any[]) {
        ls.forEach((d) => {
            try {
                const dtb = new Databin(this.coords, d.dtb);
                dtb.ownerId = d.ownerId;
                d.xitemLs.forEach((xi: any) => {
                    const xitm = new XItem(this.coords, xi);
                    xitm.ownerId = d.ownerId;
                    dtb.xitemMap.set(xitm.id, xitm);
                });
                d.cLs.forEach((cData: any) => {
                    try {
                        cData.conn.xitemIdLs = cData.conn.xitemIdLs.filter((x: any) =>
                            dtb.xitemMap.has(x),
                        );
                        new Connection(this.coords, cData.conn).intoProject();
                    } catch (e) {}
                });
                dtb.sortConnections();
            } catch (e) {
                this.coords.utils$.consoleError('DATABIN$ LOAD3 ERROR', e);
            }
        });

        this.afterLoad();
    }

    afterLoad() {
        this.databinLs.forEach((dtb) => dtb.refresh(true));
        this.refreshActiveXitemSet();
        this.activateFirstDatabin();
    }

    get(databinId: string) {
        return this._databinMap.get(databinId);
    }

    getByTitle(title: string) {
        title = title.toLowerCase().trim();
        for (const dtb of this._databinMap.values()) {
            if (dtb.title.toLowerCase().trim() === title) return dtb;
        }
        return null;
    }

    async activateFirstDatabin(isFinal = false) {
        if (!isFinal) {
            await this.coords.utils$.wait(0.3);
            this.activateFirstDatabin(true);
            return;
        }
        if (!this._databinMap.size) this.new();
        this.current = this.sortedDatabinLs[0];
        this.coords.display$.markForCheck();
    }

    delete(dt: Databin) {
        this._databinMap.delete(dt.id);
        dt.manifestDelete();
        this.activateFirstDatabin();
        this.refreshActiveXitemSet();
    }

    new(params?: DTB) {
        if (!params) params = { title: 'DEFAULT' };
        const dtb = new Databin(this.coords, params);
        dtb.markForManifestStore();
        return dtb;
    }

    registerDatabin(dt: Databin) {
        this._databinMap.set(dt.id, dt);
    }

    registerConnector(con: Connector) {
        this._connectorMap.set(con.tag, con);
    }

    getConnector(connectorTag: string) {
        return this._connectorMap.get(connectorTag);
    }

    clear() {
        this.coords.dataset$.preview.isReady = false;
        this._databinMap.clear();
        this.activeXitemIdSet.clear();
        // this.refreshActiveXitemSet()
        this.current = undefined;
        this.editedConnection$.next(undefined);
    }

    async import() {
        const inp = new ScriptInput(this.coords);
        await inp.readFile();
        inp.importDatabins(true);
    }

    async importResponse(params: any) {
        const mp: Map<string, any[]> = params.resp;
        const dtbData = mp.get('databin')![0];
        // dtbData.title = `${this.coords.text("imported")} ${dtbData.title}`
        const dtb = new Databin(this.coords, dtbData);
        const xitmLsData = mp.get('databin-xitemLs')![0];
        xitmLsData.xitemLs.forEach((xi: any) => {
            const xitm = new XItem(this.coords, xi);
            xitm.ownerId = this.coords.user$.currentId;
            dtb.xitemMap.set(xitm.id, xitm);
        });
        const conIdLsData = mp.get('connection-xitemIdLs');
        if (conIdLsData) {
            const conIdLsMap = new Map<string, string[]>();
            conIdLsData.forEach((x: any) => conIdLsMap.set(x.id, x.xitemIdLs));
            const conData = mp.get('connection');
            conData &&
                conData.forEach((c: any) => {
                    c.xitemIdLs = conIdLsMap.get(c.id) || [];
                    const con = new Connection(this.coords, c);
                    con.finalize();
                });
            this.refreshActiveXitemSet();
            dtb.setCurrent();
        }
        // const dtb = new Databin(this.coords, )
    }

    private _exportParams() {
        const ls: [string, number][] = [];

        this.databinLs.forEach((x) => ls.push([x.title, x.size]));

        return {
            headerLs: ['Databin', 'Count'].map((x) => this.coords.text(x)),
            rowLs: ls,
            filename: `${this.coords.project$.currentTitle}_databins`,
        };
    }

    html(isPartial = false) {
        this.coords.export$.html(this._exportParams());
    }

    csv(isPartial = false) {
        this.coords.export$.csv(this._exportParams());
    }

    export() {
        this.coords.manifest$.exportPart('databins');
    }

    saveStream(wr: any) {}

    insertNewDatabin(params?: { title: string }) {
        const dtb = this.new();
        if (params?.title) dtb.set('title', params.title);
        dtb.setCurrent();
        dtb.editTitle();
        return dtb;
    }

    get databinLs() {
        return Array.from(this._databinMap.values());
    }

    get sortedDatabinLs() {
        return this.databinLs.sort((a, b) =>
            a.title?.toLowerCase() < b.title?.toLowerCase() ? -1 : 1,
        );
    }

    get activeDatabinLs() {
        return this.databinLs.filter((x) => x.isOn);
    }

    get activeConnectionLs() {
        const ls: Connection[] = [];
        this.activeDatabinLs.forEach((dtb) => {
            dtb.connectionLs.forEach((conn) => conn.isOn && ls.push(conn));
        });
        return ls;
    }

    get connectorLs() {
        return Array.from(this._connectorMap.values()).filter((x) => !x.isOnline);
    }

    get onlineConnectorLs() {
        return Array.from(this._connectorMap.values()).filter((x) => x.isOnline);
    }

    refreshActiveXitemSet() {
        const s = new Set<string>();
        const xitmLs: XItem[] = [];
        const dtbLs: Databin[] = this.activeDatabinLs.filter((dtb) => dtb.isOn);

        dtbLs.forEach((dtb) => {
            dtb.activeXitemIdSet.forEach((xitmId) => {
                if (s.has(xitmId)) return;
                s.add(xitmId);
                xitmLs.push(dtb.xitemMap.get(xitmId)!);
            });
        });

        const dt = this.coords.dataset$.preview;
        dt.clear();
        dt.importXitemLs(xitmLs);
    }
}
